<template>
    <b-card
        no-body
        bg-variant="default"
        border-variant="light"
    >
        <AddPlacementComponentModal
            ref="AddPlacementComponentModal"
            @created="hierarchyPositionComponentCreated"
        />
        <div class="card-header bg-light">
            <b-card-title>{{ substance.title }}</b-card-title>
            <div class="text-right" v-if="!readOnly">
                <ExcludeButton
                    @exclude="exclude"
                />
            </div>
        </div>
        <b-row class="p-1 pl-2">
            <b-col>
                <b-badge class="mr-1">CAS: {{ substance.cas }}</b-badge>
                <b-badge>EC: {{ substance.ec }}</b-badge>
            </b-col>
            <b-col>
                <div v-if="legislations.length > 0">
                    Regulation:
                    <b-badge style="margin-right: 8px;" v-for="legislation in legislations">
                        {{ legislation.title }}
                    </b-badge>
                </div>
            </b-col>
        </b-row>
        <b-row v-if="compactView" class="pl-2 pr-2">
            <b-col>
                <b-row>
                    <b-col md="4" lg="4" sm="2">
                        <b-form-group label="Max conc. (w/w %)">
                            <b-form-input
                                v-model="maxConcentrationRange"
                                type="number"
                                :disabled="readOnly"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="8" lg="8" sm="10">
                        <b-form-group label="SCIP interval">
                            <v-select
                                v-model="concentrationRange"
                                label="title"
                                :options="intervalOptions"
                                @input="onConcentrationRangeChange"
                                placeholder="Select interval"
                                :disabled="readOnly"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-group label="Mixture category">
                            <treeselect
                                @input="onMixtureCategoryChange"
                                :options="mixtureCategories"
                                noChildrenText=""
                                valueFormat="object"
                                :normalizer="normalize"
                                :disabled="readOnly"
                                v-model="mixtureCategory"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-group label="Material category">
                            <treeselect
                                @input="onMaterialCategoryChange"
                                :options="materialCategories"
                                noChildrenText=""
                                valueFormat="object"
                                :normalizer="normalize"
                                v-model="materialCategory"
                                :disabled="readOnly"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-group label="Additional material characteristics">
                            <v-select
                                v-model="additionalMaterial"
                                @input="onAdditionalMaterialChange"
                                label="title"
                                :getOptionLabel="(o) => o.title"
                                :options="additionalMaterials"
                                :disabled="readOnly"
                                placeholder="Select"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <b-row v-else class="pl-2 pr-2">
            <b-col md="5" lg="5">
                <b-row>
                    <b-col md="4" lg="4" sm="2">
                        <b-form-group label="Max conc. (w/w %)">
                            <b-form-input
                                v-model="maxConcentrationRange"
                                type="number"
                                :disabled="readOnly"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="8" lg="8" sm="10">
                        <b-form-group label="SCIP interval">
                            <v-select
                                v-model="concentrationRange"
                                label="title"
                                :options="intervalOptions"
                                @input="onConcentrationRangeChange"
                                placeholder="Select interval"
                                :disabled="readOnly"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-group label="Mixture category">
                            <treeselect
                                @input="onMixtureCategoryChange"
                                :options="mixtureCategories"
                                noChildrenText=""
                                valueFormat="object"
                                :normalizer="normalize"
                                :disabled="readOnly"
                                v-model="mixtureCategory"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
            </b-col>
            <b-col md="7" lg="7">
                <b-row>
                    <b-col>
                        <b-form-group label="Material category">
                            <treeselect
                                @input="onMaterialCategoryChange"
                                :options="materialCategories"
                                noChildrenText=""
                                valueFormat="object"
                                :normalizer="normalize"
                                v-model="materialCategory"
                                :disabled="readOnly"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-group label="Additional material characteristics">
                            <v-select
                                v-model="additionalMaterial"
                                @input="onAdditionalMaterialChange"
                                label="title"
                                :getOptionLabel="(o) => o.title"
                                :options="additionalMaterials"
                                :disabled="readOnly"
                                placeholder="Select"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <b-row class="pl-2 pr-2 pb-1">
            <b-col>
                <SubstancePlacement
                    :substance="substance"
                    :placement-hierarchy="element.placementHierarchy"
                    :show-add-button="canAddPlacementComponents"
                    @addComponent="addComponent"
                />
            </b-col>
        </b-row>
    </b-card>
</template>
<script>
import { BCardTitle, BListGroup, BListGroupItem, BBadge, BFormRadio, BCard, BRow, BCol, BFormGroup, BButton,
    BFormInput } from 'bootstrap-vue'
import ExcludeButton from "@/views/request/received/flow/components/concern-element/ExcludeButton";
import Ripple from 'vue-ripple-directive'
import { VueAutosuggest } from 'vue-autosuggest'
import vSelect from 'vue-select'
import Treeselect from '@riophae/vue-treeselect'
import {SubstancePlacement, AddPlacementComponentModal} from "@core/components/product";

export default {
    components: {
        SubstancePlacement, BCard, BButton, BRow, BCol, BFormGroup, BCardTitle, ExcludeButton,
        BFormInput, VueAutosuggest, vSelect, BBadge, BFormRadio, Treeselect, BListGroup, BListGroupItem,
        AddPlacementComponentModal,
    },
    directives: {Ripple},
    props: {
        compactView: {
            type: Boolean,
            default: false
        },
        element: {
            type: Object,
            required: true
        },
        additionalMaterials: {
            type: Array,
            default() {
                return [];
            }
        },
        materialCategories: {
            type: Array,
            default() {
                return [];
            }
        },
        mixtureCategories: {
            type: Array,
            default() {
                return []
            }
        },
        substanceList: {
            type: Object,
            default() {
                return {
                    id: null,
                    legislations: [],
                    concentrationRange: []
                };
            }
        },
        details: {
            type: Object,
            default() {
                return {
                    concentrationRange: null,
                    additionalMaterial: null,
                    materialCategory: null,
                    mixtureCategory: null,
                };
            }
        },
        canAddPlacementComponents: {
            type: Boolean,
            default: false
        }
    },
    data() {
        let details = this.details;

        if (this.element.details) {
            if (this.element.details instanceof Array && this.element.details.length > 0) {
                details = this.element.details[0];
            } else {
                details = this.element.details;
            }
        }

        return {
            maxConcentrationRange: null,
            concentrationRange: details.concentrationRange,
            additionalMaterial: details.additionalMaterial,
            materialCategory: details.materialCategory,
            mixtureCategory: details.mixtureCategory,

            suggestions: [],
            searchTimer: null,
            inputProps: {
                class: 'form-control w-100',
            },
        };
    },
    methods: {
        addComponent() {
            this.$refs.AddPlacementComponentModal.show();
        },
        hierarchyPositionComponentCreated(component) {
            this.$emit('hierarchyPositionComponentCreated', component, this.element)
        },
        exclude() {
            this.$emit('exclude', this.element);
        },
        normalize(node) {
            return {id: node.id, label: node.title};
        },

        onMaterialCategoryChange(value) {
            this.materialCategory = value;
            this.emit();
        },

        onMixtureCategoryChange(value) {
            this.mixtureCategory = value;
            this.emit();
        },

        onAdditionalMaterialChange(value) {
            this.additionalMaterial = value;
            this.emit();
        },

        onConcentrationRangeChange(value) {
            this.concentrationRange = value;
            this.emit();
        },

        emit() {
            // todo refactor, use objects
            this.$emit('change', this.element, {
                concentrationRange: this.concentrationRange,
                additionalMaterial: this.additionalMaterial,
                materialCategory: this.materialCategory,
                mixtureCategory: this.mixtureCategory,
                maxConcentrationRange: this.maxConcentrationRange,
            });
        },

        getSuggestionValue() {
            return '';
        },
    },
    computed: {
        legislations() {
            return [];
        },
        intervalOptions() {
            if (this.substanceList) {
                return this.substanceList.concentrationRange;
            }

            return [];
        },
        substance() {
            return this.element.substance;
        },

        readOnly() {
            return this.element.placementHierarchy && this.element.placementHierarchy.hierarchy.length > 1;
        }
    },
}
</script>
<style lang="css">
@import '../../../../../../../node_modules/@riophae/vue-treeselect/dist/vue-treeselect.css';
</style>
